import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik, FormikProps } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { useLocale } from '../../contexts/localeContext';
import plusIcon from "../../images/Plus.svg";
import { BreadcrumbLink } from '../../models/BreadcrumbLink';
import { ButtonTypes } from "../../models/ButtonTypes";
import { CanGoBack } from '../../models/CanGoBack';
import { TabSummary } from '../../models/TabSummary';
import { resetEnrollformValues } from '../../utilities/formFields';
import ActivityFrame from '../activity-frame/activity-frame';
import AutoStateUpdate from '../autoStateUpdate/AutoStateUpdate';
import Breadcrumb from '../breadcrumb/breadcrumb';
import Card from '../card/card';
import { DialogContent } from '../dialog/Dialog';
import { useDialog } from '../dialog/DialogContext';
import Tabs from '../tabs/tabs';

interface Props extends CanGoBack {
    itemProfile?: any,
    busy: boolean,
    breadcrumb: BreadcrumbLink[],
    saveDetails: (values) => void,
    tabs: TabSummary[],
    getTabContent: (index) => any,
    initialValues: any,
    children: any,
    setUpdateTable?: any,
    content?: any,
    disableSaveButton?: boolean,
    itemPath?: string,
    enableFieldClear?: boolean,
}

const SimpleItemDetails: FC<Props> = ({ setUpdateTable, itemProfile, busy, breadcrumb, saveDetails, tabs, getTabContent, onBack = (event?: any) => { }, initialValues = {}, children, content, disableSaveButton, itemPath, enableFieldClear = false }) => {
    const history = useHistory();
    const [hasChanges, setHasChanges] = useState(false);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const { translate } = useLocale();
    const [hasError, setHasError] = useState(false);
    const dialog = useDialog();
    const [tabErrorFlags, setTabErrorFlags] = useState<Array<boolean>>([]);

    // This method handles the case where the is no content at the current
    // selectedTab, if this is the case, it will set the selectedTab to 0
    const onLocalGetTabContent = (selectedTab) => {
        if (tabs.length > selectedTab) {
            return getTabContent(selectedTab)
        }
        setSelectedTab(0);
        return getTabContent(0);
    }

    const setNewSelectedTab = (tab) => {
        setUpdateTable && setUpdateTable(tab)
        setSelectedTab(tab)
    }

    /**
     * This function would allow us to show/hide the Submit button depending on whether
     * the form was dirty (i.e. edited) or not. However, while it does hide the button,
     * it never seems to bring it back again.
     */
    function showSubmitButton() {
        return (
            <div className="flex flex-row">
                <button id="saveRecordId" className={!!!hasError && !!hasChanges ? "bg-lime text-white p-2 rounded-lg shadow-lg" : "secondary-disabled cursor-not-allowed  p-2 rounded-lg shadow-lg"}
                    type="submit">
                    <FontAwesomeIcon icon={faCheck} className="mx-2 w-8"></FontAwesomeIcon>
                    <span className="mr-2" id="saveBtn">{translate.byKey(TranslationKeys.save)}</span>
                </button>
            </div >
        );
    }

    function showAddNewSubmitButton(formik: FormikProps<any>) {

        return (
            <button className="flex rounded-lg bg-lime text-white p-2 shadow-lg" id="addNewRecord" type='button' onClick={(e) => {
                // quick fix for clearing the data, this fix a should be improved
                if (selectedTab === 0 && enableFieldClear) {
                    formik.setValues(resetEnrollformValues(formik.values));
                }

                history.push(itemPath ?? '');
            }}>
                <img
                    src={plusIcon}
                    className="px-2 w-8 py-1"
                    alt="" />
                <span className="mr-2" id="newRecordBtn">{translate.byKey(TranslationKeys.add)}</span>
            </button>
        );
    }

    /**
     * Cascade for updating the state of showSubmitButton when an item is saved.
     */
    useEffect(() => {
        setHasChanges(false);

    }, [itemProfile])

    /**
    * Handle back button click.
    * Has validation to check for unsaved changes and block.
    * @param event
    */
    const handleBackClick = (event, values) => {
        event.preventDefault();
        if (hasChanges) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.unsavedChanges),
                message: translate.byKey(TranslationKeys.unsavedChangesMessage),
                buttons: [
                    {
                        id: "btn_secureMode",
                        onClick: () => {
                            let button = document?.getElementById('saveRecordId') as HTMLButtonElement
                            button?.click();
                        },
                        text: translate.byKey(TranslationKeys.save),
                        type: ButtonTypes.GreenRoundedButton
                    },
                    {
                        id: "btn_DontSave",
                        onClick: onBack,
                        text: translate.byKey(TranslationKeys.dontSave),
                        type: ButtonTypes.BlueRoundedButton,
                    },
                    {
                        id: "btn_Cancel",
                        onClick: () => { }, // Empty onClick will just close the dialog
                        text: translate.byKey(TranslationKeys.cancel),
                        type: ButtonTypes.TextButton,
                        className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                    }
                ]
            }
            dialog.pushInfo(dialogContent);
        }
        else {
            onBack(event);
        }
    }

    useEffect(() => {
        setTabErrorFlags(new Array(tabs.length).fill(false));
    }, [tabs]);

    // If any tab has an error (represented by 'true' in 'tabErrorFlags'),
    // set the 'hasError' state to true, otherwise, set it to false.
    useEffect(() => {
        const errorIsFound: boolean = tabErrorFlags.includes(true);
        setHasError(errorIsFound);
    }, [tabErrorFlags]);

    return <>
        <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={saveDetails} >
            {(formik: FormikProps<any>) => {
                const {
                    values,
                    submitForm,
                } = formik;
                return (<div className='flex flex-grow w-full' id='pageContainer'>
                    <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
                        <Breadcrumb
                            path={breadcrumb}
                            onClick={item => history.push(item.value)}
                            className="bg-darker" />

                        <Form id='ss-form' autoComplete='off' className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                            <Card hasBorder={false} hasShadow={false}>
                                {children}
                            </Card>
                            {tabs.length > 0 ?
                                <>
                                    <Tabs tabs={tabs} onSelect={i => setNewSelectedTab(i)} selected={selectedTab} add={false} onAdd={() => { }} setSelectedTab={setSelectedTab} itemProfile={itemProfile} className="pt-2">
                                        <div className="w-full h-full">
                                            {onLocalGetTabContent(selectedTab)}
                                        </div>
                                    </Tabs>
                                </> :
                                <div className="flex flex-col px-8 ">
                                    {content}
                                </div>}
                            <div className="flex flex-row justify-end button-cluster">
                                <AutoStateUpdate setHasChanges={setHasChanges} setTabErrorFlags={setTabErrorFlags} selectedTab={selectedTab}></AutoStateUpdate>
                                {!!!disableSaveButton && !!hasChanges ? showSubmitButton() : null}
                                {!!!disableSaveButton && !hasChanges && values.id && itemPath ? showAddNewSubmitButton(formik) : null}
                                <div className="flex flex-row justify-end ml-3">
                                    <button id="backBtn" type="button" className="ml-2 bg-portal-blue-primary-shade2 text-white p-2 rounded-lg shadow-lg" onClick={(e) => handleBackClick(e, values)}>
                                        <FontAwesomeIcon icon={faArrowLeft} className="mx-2 w-8"></FontAwesomeIcon>
                                        <span className="mr-2">{translate.byKey(TranslationKeys.back)}</span>
                                    </button>
                                </div>
                            </div>
                        </Form>

                    </ActivityFrame>
                </div>
                );
            }}
        </Formik>
    </>;
};

export default SimpleItemDetails;
